/**
 * Displays species candidates for user selection in livestock chat flow.
 *
 * Features:
 * - Shows best match prominently with Add button
 * - Displays scientific name for species
 * - Shows link to view alternative matches
 * - Includes "Add as custom" button for custom entries
 * - Handles loading state by disabling all buttons
 */
import { Paper, Stack, Text, Button, Group, Anchor, Box } from '@mantine/core';
import { IconFish, IconPlus } from '@tabler/icons-react';
import type {
  SpeciesCandidate,
  LivestockParsedData,
  ConfirmLivestockAddRequest,
} from '@/api/chat';

interface LivestockSelectionMessageProps {
  candidates: SpeciesCandidate[];
  parsedData: LivestockParsedData;
  onSelect: (data: ConfirmLivestockAddRequest) => void;
  onViewAlternatives?: (searchQuery: string) => void;
  loading: boolean;
}

export function LivestockSelectionMessage({
  candidates,
  parsedData,
  onSelect,
  onViewAlternatives,
  loading,
}: LivestockSelectionMessageProps) {
  const today: string = new Date().toISOString().split('T')[0] ?? '';

  const handleSelect = (candidate: SpeciesCandidate) => {
    onSelect({
      species_id: candidate.id,
      common_name: candidate.common_name,
      scientific_name: candidate.scientific_name,
      quantity: parsedData.quantity,
      acquisition_date: parsedData.date ?? today,
      vendor: parsedData.vendor,
      price_paid: parsedData.price,
      currency: parsedData.currency,
    });
  };

  const handleCustomEntry = () => {
    onSelect({
      species_id: null,
      common_name: parsedData.species_query ?? 'Unknown Fish',
      quantity: parsedData.quantity,
      acquisition_date: parsedData.date ?? today,
      vendor: parsedData.vendor,
      price_paid: parsedData.price,
      currency: parsedData.currency,
    });
  };

  const bestMatch = candidates[0];
  const alternativeCount = candidates.length - 1;

  return (
    <Paper p="sm" radius="md" withBorder>
      <Stack gap="xs">
        <Group gap="xs">
          <IconFish size={16} />
          <Text size="sm" fw={500}>
            {candidates.length > 0
              ? `Adding ${parsedData.quantity} to your tank`
              : 'No matching species found'}
          </Text>
        </Group>

        {bestMatch && (
          <Paper p="xs" withBorder radius="sm" bg="gray.0">
            <Group justify="space-between" align="flex-start">
              <Box>
                <Text size="sm" fw={500}>{bestMatch.common_name}</Text>
                {bestMatch.scientific_name && (
                  <Text size="xs" c="dimmed" fs="italic">{bestMatch.scientific_name}</Text>
                )}
              </Box>
              <Button
                size="xs"
                leftSection={<IconPlus size={14} />}
                disabled={loading}
                onClick={() => handleSelect(bestMatch)}
              >
                Add
              </Button>
            </Group>
          </Paper>
        )}

        {alternativeCount > 0 && onViewAlternatives && (
          <Text size="xs" c="dimmed" ta="center">
            <Anchor
              component="button"
              size="xs"
              onClick={() => onViewAlternatives(parsedData.species_query ?? '')}
            >
              {alternativeCount} other {alternativeCount === 1 ? 'match' : 'matches'} found — click to view all
            </Anchor>
          </Text>
        )}

        <Button
          variant="subtle"
          size="xs"
          fullWidth
          disabled={loading}
          onClick={handleCustomEntry}
        >
          Add as custom: "{parsedData.species_query}"
        </Button>
      </Stack>
    </Paper>
  );
}
